import { Hyperlink } from '@strata/react-hyperlink';

import { Columns } from 'components/shared/Columns';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const introduction = [
    <p key="1">
        Check out the <Hyperlink linkText="BEM Naming Methodology" navUrl="https://getbem.com" />{' '}
        website for an overview of how the naming convention helps make our front-end code easier to
        read and understand, easier to work with, easier to scale, more robust and explicit, and a
        lot more strict.
    </p>,
    <p key="2">BEM stands for Block, Element, Modifier.</p>,
];

const overview = [
    <Columns
        key="3"
        columns={Columns.ColumnNumber.THREE}
        content={
            <>
                <div>
                    <h3>Block</h3>
                    <p>Standalone part of the interface.</p>
                </div>
                <div>
                    <h3>Element</h3>
                    <p>
                        A part of a block that has no standalone meaning and is semantically tied to
                        its block.
                    </p>
                </div>
                <div>
                    <h3>Modifier</h3>
                    <p>A flag on a block or element. Used to change the appearance or behavior.</p>
                </div>
            </>
        }
    />,
];

const examples = [
    <Columns
        key="4"
        columns={Columns.ColumnNumber.THREE}
        content={
            <>
                <div>
                    <h3>Block</h3>
                    <ul>
                        <li>Button</li>
                        <li>Container</li>
                        <li>Card</li>
                        <li>Menu</li>
                    </ul>
                </div>
                <div>
                    <h3>Element</h3>
                    <ul>
                        <li>Button Label</li>
                        <li>Row</li>
                        <li>Card Header</li>
                        <li>Menu Item</li>
                    </ul>
                </div>
                <div>
                    <h3>Modifier</h3>
                    <ul>
                        <li>Disabled</li>
                        <li>Hover</li>
                        <li>Error</li>
                        <li>Visited</li>
                    </ul>
                </div>
            </>
        }
    />,
];

const classExample = [
    <div key="5" className="sds_bem__code padding__around--medium">
        <span className="sds_bem__code__class">.button__label--disabled</span>{' '}
        <span className="sds_bem__code__character">{'{'}</span>
        <br />
        <span className="sds_bem__code__property margin__left--medium">color</span>
        <span className="sds_bem__code__character">:</span>{' '}
        <span className="sds_bem__code__value">$color__button-label--disabled</span>
        <span className="sds_bem__code__character">;</span>
        <br />
        <span className="sds_bem__code__character">{'}'}</span>
    </div>,
];

const namingConventions = [
    <div key="6">
        <p>
            Naming conventions make our code more readable, maintainable, and organized. A few
            things to keep in mind while writing Sass with BEM.
        </p>
        <ul>
            <li>
                To avoid naming conflicts with other libraries, we use a global namespace. Our
                classes are prefixed with .sds_ (Strata Design System).
            </li>
            <li>Names are written in lowercase and words are separated by a hyphen (-).</li>
        </ul>
    </div>,
    <div key="7">
        <h3>Sass Example</h3>
        <div className="sds_bem__code padding__around--medium">
            <span className="sds_bem__code__class">.sds_button</span>{' '}
            <span className="sds_bem__code__character">{'{'}</span>
            <br />
            <span className="sds_bem__code__property margin__left--medium">background-color</span>
            <span className="sds_bem__code__character">:</span>{' '}
            <span className="sds_bem__code__value">$color__button--primary</span>
            <span className="sds_bem__code__character">;</span>
            <br />
            <br />
            <span className="sds_bem__code__class margin__left--medium">&__label</span>{' '}
            <span className="sds_bem__code__character">{'{'}</span>
            <br />
            <span className="sds_bem__code__property margin__left--medium">color</span>
            <span className="sds_bem__code__character">:</span>{' '}
            <span className="sds_bem__code__value">$color__button-label—-primary</span>
            <span className="sds_bem__code__character">;</span>
            <br />
            <span className="sds_bem__code__character margin__left--medium">{'}'}</span>
            <br />
            <span className="sds_bem__code__class margin__left--medium">&--disabled</span>{' '}
            <span className="sds_bem__code__character">{'{'}</span>
            <br />
            <span className="sds_bem__code__property margin__left--medium">background-color</span>
            <span className="sds_bem__code__character">:</span>{' '}
            <span className="sds_bem__code__value">$color__button—-disabled</span>
            <span className="sds_bem__code__character">;</span>
            <br />
            <span className="sds_bem__code__property margin__left--medium">color</span>
            <span className="sds_bem__code__character">:</span>{' '}
            <span className="sds_bem__code__value">$color__button-label--disabled</span>
            <span className="sds_bem__code__character">;</span>
            <br />
            <span className="sds_bem__code__character margin__left--medium">{'}'}</span>
            <br />
            <span className="sds_bem__code__character">{'}'}</span>
        </div>
    </div>,
];

export const BemMethodology = () => (
    <div>
        <Page
            pageTitle="BEM Methodology"
            pageDescription="An overview of following BEM naming conventions."
            pageContent={
                <>
                    <Section title="Introduction" contentArray={introduction} />
                    <Section title="Overview" contentArray={overview} />
                    <Section title="Examples" contentArray={examples} />
                    <Section title="BEM Class Example" contentArray={classExample} />
                    <Section title="Naming Conventions" contentArray={namingConventions} />
                </>
            }
        />
    </div>
);
